import React from 'react';
import { MapPin, Factory, Anchor, HardHat, Phone } from 'lucide-react';
type Region = {
  name: string;
  icon: React.ElementType;
  summary: string;
  sites: string[];
};
const regions: Region[] = [{
  name: 'Los Angeles & Long Beach',
  icon: Anchor,
  summary: 'Decades of shipbuilding, oil refining, and heavy construction made Los Angeles County one of the most asbestos-contaminated regions in the United States.',
  sites: ['Port of Los Angeles & Long Beach Naval Shipyard', 'Chevron El Segundo & Wilmington refineries', 'Todd Shipyards, San Pedro', 'Los Angeles Department of Water & Power plants', 'Aerospace and auto plants across the San Fernando Valley']
}, {
  name: 'San Francisco Bay Area',
  icon: Factory,
  summary: 'Bay Area shipyards, power stations, and refineries used raw asbestos insulation well into the 1980s, exposing tens of thousands of tradesmen.',
  sites: ['Hunters Point Naval Shipyard, San Francisco', 'Mare Island Naval Shipyard, Vallejo', 'Chevron Richmond Refinery', 'Kaiser Shipyards, Richmond', 'PG&E power generating stations']
}, {
  name: 'San Diego & Coastal Southern California',
  icon: Anchor,
  summary: 'Navy service members, civilian shipfitters, and dockworkers in San Diego handled asbestos gaskets, pipe lagging, and boiler insulation daily.',
  sites: ['Naval Base San Diego & 32nd Street Naval Station', 'NASSCO Shipyard', 'Naval Air Station North Island, Coronado', 'San Onofre Nuclear Generating Station', 'Camp Pendleton facilities']
}, {
  name: 'Central Valley & Sacramento',
  icon: HardHat,
  summary: 'Agricultural processing, cement production, and public works projects across the Central Valley relied heavily on asbestos-containing materials.',
  sites: ['Sacramento Army Depot & McClellan Air Force Base', 'Fresno and Stockton food processing plants', 'Cement and pipe manufacturing facilities', 'Rail yards in Roseville, Bakersfield, and Stockton', 'School, hospital, and municipal building projects']
}, {
  name: 'Inland Empire & Kern County',
  icon: Factory,
  summary: 'Steel mills, refineries, and oilfield operations exposed generations of workers to airborne asbestos fibers with little or no protective equipment.',
  sites: ['Kaiser Steel Mill, Fontana', 'Kern County oilfields and pump stations', 'Riverside and San Bernardino cement plants', 'Norton Air Force Base', 'Regional power and utility installations']
}, {
  name: 'North Coast & Rural California',
  icon: HardHat,
  summary: 'Lumber mills, paper plants, and naturally occurring asbestos deposits have produced mesothelioma claims well outside California’s major metros.',
  sites: ['Humboldt and Mendocino lumber and pulp mills', 'Geothermal and hydroelectric plants', 'El Dorado and Calaveras County serpentine deposits', 'Rural school and hospital construction sites', 'Highway and tunnel construction projects']
}];
export function ExposureSites() {
  return <section id="exposure-sites" className="py-20 md:py-24 bg-brand-50 border-y border-brand-100" data-id="element-99" data-mp="1tf1ywr3vp8i8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-100" data-mp="1phcq3q5fw9us">
        <div className="max-w-3xl mx-auto text-center mb-14" data-id="element-101" data-mp="1kgyqmwhwm88d">
          <div className="inline-flex items-center justify-center p-3 bg-white rounded-full mb-4 border border-brand-100" data-id="element-102" data-mp="1fpqehsp6w8ri">
            <MapPin className="w-7 h-7 text-brand-900" data-id="element-103" data-mp="1arf6lnigg8bx" />
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-900 mb-4" data-id="element-104" data-mp="15rkg3l2ldkre">
            California Asbestos Exposure Sites
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed" data-id="element-105" data-mp="11p5y136qyp16">
            Asbestos claims have been filed by mesothelioma victims who lived or
            worked in these California locations. From the shipyards of San
            Diego and the Bay Area to refineries, steel mills, power plants, and
            construction sites across the Central Valley, asbestos was used
            heavily throughout the state until the 1980s — and workers were
            rarely warned about the danger.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-14" data-id="element-106" data-mp="2rzsp08b3ro6t">
          {regions.map(region => {
          const Icon = region.icon;
          return <article key={region.name} className="bg-white rounded-xl p-8 border border-brand-100 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-id="element-107" data-mp="2i5irkc7azzp8">
                <div className="flex items-center gap-3 mb-4" data-id="element-108" data-mp="293j9rhogsrl8">
                  <div className="bg-brand-900 p-2.5 rounded-lg shrink-0" data-id="element-109" data-mp="2duzau38ivcr">
                    <Icon className="w-5 h-5 text-brand-accent" data-id="element-110" data-mp="7cg12xy4xn3b" />
                  </div>
                  <h3 className="text-lg font-serif font-bold text-brand-900 leading-tight" data-id="element-111" data-mp="hflcdpachsey">
                    {region.name}
                  </h3>
                </div>

                <p className="text-sm text-slate-600 leading-relaxed mb-5" data-id="element-112" data-mp="cenkgkzj9k09">
                  {region.summary}
                </p>

                <ul className="space-y-2 mt-auto" data-id="element-113" data-mp="lhop0h8r331t">
                  {region.sites.map(site => <li key={site} className="flex items-start gap-2 text-sm text-slate-700" data-id="element-114" data-mp="1062tewltezkh">
                      <MapPin className="w-4 h-4 text-brand-accent shrink-0 mt-0.5" data-id="element-115" data-mp="v9cb02mjfrvw" />
                      <span data-id="element-116" data-mp="196hf3x2oygoi">{site}</span>
                    </li>)}
                </ul>
              </article>;
        })}
        </div>

        <div className="bg-brand-900 rounded-2xl p-8 md:p-10 text-white" data-id="element-117" data-mp="3axm4emh5j55o">
          <div className="grid lg:grid-cols-3 gap-8 items-center" data-id="element-118" data-mp="36x26hgor7xih">
            <div className="lg:col-span-2 space-y-4" data-id="element-119" data-mp="36ylq9h9mcqp0">
              <h3 className="text-2xl font-serif font-bold text-white" data-id="element-120" data-mp="19wy0ocgwmm6c">
                Don’t see your workplace listed?
              </h3>
              <p className="text-brand-100 leading-relaxed" data-id="element-121" data-mp="1evhm8brl5vy6">
                This list is only a starting point. California has thousands of
                documented asbestos job sites, and exposure also reached family
                members through fibers carried home on work clothes — known as
                secondary or take-home exposure. Trades most at risk include
                boilermakers, pipefitters, insulators, electricians, welders,
                machinists, auto mechanics, drywallers, longshoremen, refinery
                operators, and U.S. Navy veterans.
              </p>
              <p className="text-brand-100 leading-relaxed" data-id="element-122" data-mp="27lznr8cnvp9h">
                Our investigators maintain a detailed database of California
                exposure sites, product identifications, and employer records
                going back to the 1940s. Even if you are unsure where you were
                exposed, we can trace it for you at no cost.
              </p>
            </div>

            <div className="flex flex-col gap-3" data-id="element-123" data-mp="uctccfqve4w">
              <a href="tel:805-770-9317" className="inline-flex justify-center items-center gap-2 bg-brand-accent hover:bg-brand-accentHover text-brand-900 px-6 py-4 rounded-md font-bold text-lg transition-colors" data-id="element-124" data-mp="3s1fmvaj21wia">
                <Phone className="w-5 h-5" data-id="element-125" data-mp="onbkwlb5ngu2" />
                (805) 770-9317
              </a>
              <a href="#contact" className="inline-flex justify-center items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-6 py-4 rounded-md font-bold transition-colors" data-id="element-126" data-mp="jo8nnn1f2ewn">
                Check Your Exposure Site
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}